import React, { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  MenuItem,
  FormControlLabel,
  Switch,
  alpha,
  useTheme,
  Avatar,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";

import CustomInput from "../../CustomForm/CustomInput";
import CustomDate from "../../CustomForm/CustomDate";
import { QK } from "../../../base/qk";
import { createProfessionalContact } from "../../../api/services/Client";

const ProfessionalModal = ({ show, onHide }) => {
  const theme = useTheme();
  const [userInput, setUserInput] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { id } = useParams();

  const mutation = useMutation(createProfessionalContact, {
    onSuccess: () => {
      enqueueSnackbar("Professional Contact created successfully", {
        variant: "success",
      });
      queryClient.invalidateQueries("professionalContacts");
      setUserInput({});
      onHide();
    },
    onError: (error) => {
      console.error(`Error: ${error.message}`);
      enqueueSnackbar("Failed to create Professional Contact", {
        variant: "error",
      });
    },
  });

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    const inputValue =
      type === "file" ? files[0] : type === "checkbox" ? checked : value;

    setUserInput((prevFormData) => ({
      ...prevFormData,
      [name]: inputValue,
    }));
  };

  const handleSave = () => {
    const inputData = new FormData();
    inputData.append("client", id);
    for (const key in userInput) {
      if (userInput[key] !== undefined) {
        inputData.append(key, userInput[key]);
      }
    }
    mutation.mutate(inputData);
  };

  return (
    <Dialog
      open={show}
      onClose={onHide}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          overflowY: "visible",
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          overflowY: "visible",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              }}
            >
              <PersonAddAlt1Icon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ color: "#1A1A2E" }}
              >
                Add Professional Contact
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Enter details for the new professional contact
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onHide}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2, overflowY: "visible" }}>
        {/* 🔹 3 fields per row on desktop, stacks on smaller screens */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {/* Row 1 — First / Last / Telephone */}
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="First Name *"
              name="first_name"
              value={userInput.first_name || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="Last Name *"
              name="last_name"
              value={userInput.last_name || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="Telephone *"
              name="telephone"
              value={userInput.telephone || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>

          {/* Row 2 — Mobile / Email / Role */}
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="Mobile"
              name="mobile"
              value={userInput.mobile || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="Email"
              name="email"
              value={userInput.email || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              select
              label="Role"
              name="role"
              value={userInput.role || ""}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value="">Select your role</MenuItem>
              <MenuItem value="Option 1">Option 1</MenuItem>
              <MenuItem value="Option 2">Option 2</MenuItem>
            </CustomInput>
          </Box>

          {/* Row 3 — Special / Team / Organization */}
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="Special"
              name="special"
              value={userInput.special || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="Team"
              name="team"
              value={userInput.team || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomInput
              label="Organization"
              name="organization"
              value={userInput.organization || ""}
              onChange={handleChange}
              fullWidth
            />
          </Box>

          {/* Row 4 — Start Date / End Date / Key Worker */}
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomDate
              label="Start Date"
              name="start_date"
              value={userInput.start_date || ""}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ flex: "1 1 calc(33.333% - 1.34rem)", minWidth: "200px" }}>
            <CustomDate
              label="End Date"
              name="end_date"
              value={userInput.end_date || ""}
              onChange={handleChange}
            />
          </Box>
          <Box
            sx={{
              flex: "1 1 calc(33.333% - 1.34rem)",
              minWidth: "200px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  name="key_worker"
                  color="primary"
                  checked={!!userInput.key_worker}
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: "key_worker",
                        type: "checkbox",
                        checked: e.target.checked,
                      },
                    })
                  }
                />
              }
              label={
                <Typography variant="body2" fontWeight={600}>
                  Key Worker
                </Typography>
              }
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
        <Button
          onClick={onHide}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "10px",
            borderColor: "#D0D5DD",
            color: "#344054",
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={mutation.isLoading}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            bgcolor: "#2469F0",
            px: 4,
            "&:hover": {
              bgcolor: "#1A5BD4",
              boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
            },
          }}
        >
          {mutation.isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Save Contact"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfessionalModal;